"use client";

import { useState } from "react";
import { Loader } from "@/components/common/loader";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { DietMenuAllergensToggle } from "@/features/diets/components/diet-menu-modal/diet-menu-allergens-toggle";
import { DietMenuCalorificBadge } from "@/features/diets/components/diet-menu-modal/diet-menu-calorific-badge";
import { DietMenuCalorificSelect } from "@/features/diets/components/diet-menu-modal/diet-menu-calorific-select";
import { DietMenuMealsList } from "@/features/diets/components/diet-menu-modal/diet-menu-meals-list";
import { DietMenuModalIngredients } from "@/features/diets/components/diet-menu-modal/diet-menu-modal-ingredients";
import type { DietCatalogDetail } from "@/features/diets/types";
import type { DietMenuPreview } from "@/features/diets/types/diet-menu-preview.type";
import { useMobile } from "@/lib/hooks/use-mobile";

type DietMenuModalBodyProps = {
  dietId: string;
  setActiveMealId: (mealId: string | null) => void;
  setActiveMealName: (mealName: string | null) => void;
  setActiveMealIngredients: (ingredients: string | null) => void;
  setActiveMealIngredientsId: (ingredientsId: number | null) => void;
  activeMealIngredients: string | null;
  activeMealIngredientsId: number | null;
  activeMealId: string | null;
  menu: DietMenuPreview | undefined;
  isMenuLoading: boolean;
  availableCalorifics?: Array<{ id: string; calories: number }>;
  calories: {
    id: string;
    calories: number;
  };
  type?: "multidiet" | "default";
  setCalories: (calories: { id: string; calories: number }) => void;
  data: DietCatalogDetail;
};

export const DietMenuModalBody = ({
  dietId,
  setActiveMealId,
  setActiveMealName,
  setActiveMealIngredients,
  setActiveMealIngredientsId,
  activeMealIngredients,
  activeMealIngredientsId,
  activeMealId,
  menu,
  isMenuLoading,
  availableCalorifics,
  calories,
  type,
  setCalories,
  data,
}: DietMenuModalBodyProps) => {
  const { isMobileExtended } = useMobile();

  const [activeAllergensAlert, setActiveAllergensAlert] = useState(false);
  const [activeDishId, setActiveDishId] = useState<string | null>(null);

  const meals = menu?.meals ?? [];

  const handleSetActiveMealIngredients = (mealId: string, dishId: string) => {
    setActiveMealId(mealId);
    setActiveDishId(dishId);

    const menuMeal = menu?.meals.find((meal) => meal.mealId === mealId);
    const dish =
      menuMeal?.dishes.find((d) => d.id === dishId) ?? menuMeal?.dishes[0];

    setActiveMealName(dish?.name ?? null);
    setActiveMealIngredients(menuMeal?.label ?? null);
    setActiveMealIngredientsId(dish?.idForIngredients ?? null);
  };

  if (isMenuLoading) {
    return <Loader />;
  }

  if (activeMealIngredients && activeMealIngredientsId) {
    return (
      <DietMenuModalIngredients
        dietId={dietId}
        dishId={activeDishId}
        mealId={activeMealId}
        meals={menu?.meals ?? []}
      />
    );
  }

  const calorificOptions = (availableCalorifics ?? []).length
    ? (availableCalorifics ?? [])
    : [calories];

  const handleCalorificChange = (id: string) => {
    const selected = calorificOptions.find((option) => option.id === id);
    if (!selected) {
      return;
    }

    setCalories(selected);
  };
  // This fixes a bug with the disappearing button in the carousel on mobile devices
  const ScrollAreaComponent = isMobileExtended ? "div" : ScrollArea;

  return (
    <ScrollAreaComponent className="pb-8 max-md:px-4 max-md:pt-6 md:h-[calc(100dvh-350px)] md:px-8 md:py-4">
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <DietMenuAllergensToggle
            checked={activeAllergensAlert}
            onCheckedChange={setActiveAllergensAlert}
          />
          {type === "multidiet" ? (
            <DietMenuCalorificBadge calories={calories.calories} />
          ) : (
            <DietMenuCalorificSelect
              calorificOptions={calorificOptions}
              calories={calories}
              onCalorificChange={handleCalorificChange}
            />
          )}
        </div>
        <DietMenuMealsList
          activeAllergensAlert={activeAllergensAlert}
          data={data}
          meals={meals}
          setActiveMealIngredients={handleSetActiveMealIngredients}
          type={type}
        />
      </div>
      {!isMobileExtended && <ScrollBar orientation="vertical" />}
    </ScrollAreaComponent>
  );
};
